import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Calendar as BigCalendarComponent,
  momentLocalizer,
  Navigate as navigate,
} from 'react-big-calendar';
import moment from 'moment';
import { Modal, Tooltip } from 'react-bootstrap';
import { GetEventRequestDTO, useEvent } from '@/features/events/api/getEvents';
import { AppointmentResponse, CustomEvent, Event } from '@/features/events/types';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useAuth } from '@/lib/auth';
import Utils from '@/utils/util';
import { UserContext } from '@/routes';
import PetInfo from '@/features/petAttributes/components/PetInfo';
import BoardingPetInfo from '@/features/petAttributes/components/BoardingPetInfo';
import { ReactComponent as CloseIcon } from '@/assets/img/close-icon-new-v2.svg';
import { ReactComponent as EyeIcon } from '@/assets/img/eye-icon.svg';

import { Heading } from '@/components/Elements/Heading';

const mLocalizer = momentLocalizer(moment);

const localizer = momentLocalizer(moment);
type BookedSlotProps = {
  selectedDate: Date;
  selectedServiceProviderId?: string;
  isRoomBooking: boolean;
};
export const BookedSlots: React.FC<BookedSlotProps> = ({
  selectedDate,
  selectedServiceProviderId,
  isRoomBooking,
}) => {
  const { user } = useAuth();
  const userContext = useContext(UserContext);

  const useGetEventsMutation = useEvent();
  const [customEvents, setCustomeEvents] = useState<CustomEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  useEffect(() => {
    if (isRoomBooking) {
      return;
    }
    if (selectedDate && selectedServiceProviderId) {
      fetchEvents();
    } else {
      setCustomeEvents([]);
    }
  }, [selectedDate, selectedServiceProviderId]);

  const fetchEvents = async () => {
    const request: GetEventRequestDTO = {
      businessId: user?.businessId,
      page: 0,
      perPage: 20,
      tabSelected: 1,
      isRoomBookings: isRoomBooking,
      order: 'asc',
      sort: 'startTime',
      startDate: moment(selectedDate).startOf('day').toDate(),
      endDate: moment(selectedDate).endOf('day').toDate(),
      selectedServiceProviderId: selectedServiceProviderId,
    };

    const res: AppointmentResponse = await useGetEventsMutation.mutateAsync(request);
    constructCalendarEvents(res.list);
  };

  const constructCalendarEvents = (data: Event[]) => {
    const arr: CustomEvent[] = [];

    data &&
      data.forEach((ce: Event, index: number) => {
        const petData: any = ce.pets && ce.pets.length ? ce.pets[0] : [ce.pet];
        const event: CustomEvent = {
          id: ce.id,
          title: ce.room
            ? `${ce.room ? `${ce.room.name}` : ''} `
            : `${petData && petData?.name !== 'undefined' ? `${petData?.name} - ` : ''}  ${
                ce.petParent ? ce.petParent.name : ''
              }`,
          start: Utils.fromServerTz(ce.startTime, user?.activeLocation?.timezone),
          end: Utils.fromServerTz(ce.endTime, user?.activeLocation?.timezone),
          startTime: ce.startTime,
          endTime: ce.endTime,
          allDay: false,
          color: ce.service ? ce.service?.color : ce.room?.color,
          serviceProvider: ce.businessUser,
          pet: ce.pet ? ce.pet : undefined,
          pets: ce.pets ? ce.pets : [],
          petParent: ce.petParent ? ce.petParent : undefined,
          duration: ce?.duration ? ce?.duration : 0,
          appt: ce,
          room: ce.room,
          accomodationType: ce.accomodationType,
        };

        if (ce.service?.name || ce.room?.name) arr.push(event);
      });
    setCustomeEvents(arr);
  };
  const handleClose = () => setShowCalendar(false);
  const handleShow = () => setShowCalendar(true);

  const CustomEvent = (data: any) => {
    const closeOverlay = () => {
      document.body.click();
    };
    const compRef = useRef(null);
    const [currentHeight, setCurrentHeight] = useState(0);
    const [currentWidth, setCurrentWidth] = useState(0);

    useEffect(() => {
      const component: any = compRef.current;
      setCurrentHeight(component?.offsetHeight);
      setCurrentWidth(component?.offsetWidth);
    }, []);

    const getAppointmentDetails = () => {
      return (
        <div
          className={`appointment-detail ${data.event.room ? 'room' : 'event'}`}
          style={{ borderBottomColor: data.event?.color }}
        >
          <div className="action">
            <CloseIcon className="close-icon" onClick={closeOverlay} />
          </div>
          {data.event?.accomodationType ? (
            <div className="item">
              <BoardingPetInfo
                data={data.event.pet ? [data.event.pet] : data.event.pets}
                accomodationType={data.event.accomodationType}
              />
            </div>
          ) : (
            <div className="heading">
              <span>
                <span className="service-color" style={{ background: data.event?.color }}></span>
              </span>
              <span>
                <h5>{data.title}</h5>
                <span className="date">
                  {Utils.formatDateStringTz(data.event.startTime, user?.activeLocation?.timezone)}
                </span>
              </span>
            </div>
          )}

          <div className="details">
            <div className="item">
              <p className="label">Owner detail</p>
              <p>{data.event.petParent.name}</p>
            </div>

            {data.event.room ? (
              <>
                <div className="item">
                  <p className="label">Check-in</p>
                  <p>
                    {' '}
                    {Utils.formatDateStringTz(
                      data?.event?.startTime,
                      user?.activeLocation?.timezone,
                      "DD MMM 'yy, hh:mmA "
                    )}
                  </p>
                </div>
                <div className="item">
                  <p className="label">Check-out</p>
                  <p>
                    {' '}
                    {Utils.formatDateStringTz(
                      data?.event?.endTime,
                      user?.activeLocation?.timezone,
                      "DD MMM 'yy, hh:mmA "
                    )}
                  </p>
                </div>
                <div className="item">
                  <p className="label">Room</p>
                  <p>{data.event.room.name}</p>
                </div>
              </>
            ) : (
              <>
                <div className="item">
                  <p className="label">Pet Detail</p>
                  <PetInfo
                    data={
                      data.event.pet
                        ? data.event.pet
                        : data.event.pets && data.event.pets.length
                        ? data.event.pets[0]
                        : undefined
                    }
                    variant="small"
                    showPetBreed={true}
                  />
                </div>
                <div className="item">
                  <p className="label">{data.event.room ? 'Booked Time' : 'Appointment'}</p>
                  <p>
                    {data.event &&
                      Utils.getAMPMtimeWithoutFormat(
                        data.event.startTime,
                        user?.activeLocation?.timezone
                      )}{' '}
                    -{' '}
                    {data.event &&
                      Utils.getAMPMtimeWithoutFormat(
                        data.event.endTime,
                        user?.activeLocation?.timezone
                      )}{' '}
                    <span className="text-gray-500 text-sm">
                      {Utils.getTzAbbr(user?.activeLocation?.timezone)}
                    </span>
                  </p>
                </div>
                <div className="item">
                  <p className="label">Duration</p>
                  <p>{data.event && Utils.minutesToDhm(data.event.duration)} </p>
                </div>
                <div className="item">
                  <p className="label">Status</p>
                  <p className={'status ' + status.toLowerCase()}>{status}</p>
                </div>
              </>
            )}
            {status.toLowerCase() === 'rejected' && (
              <div className="item">
                <p className="label">Rejection Note</p>
                <p>{data.event?.appt?.rejectNote ? data.event?.appt?.rejectNote : '-'}</p>
              </div>
            )}
          </div>
        </div>
      );
    };
    const renderTooltip = (props: any) => (
      <Tooltip id="button-tooltip" className="service-tooltip" {...props}>
        {getAppointmentDetails()}
      </Tooltip>
    );
    return userContext?.isLandscape ? (
      <div
        className="services-calendar"
        ref={compRef}
        style={{
          background: data.event.color + '99',
          paddingTop: currentHeight < 20 ? '0px' : '5px',
          paddingBottom: currentHeight < 20 ? '0px' : '5px',
        }}
      >
        {' '}
        <strong> {data.title} </strong>{' '}
        {/* {currentHeight > 50 && currentWidth > 160 && (
            <p className="time-interval">
              {moment(data.event.start).format('h:mm a')} -{' '}
              {moment(data.event.end).format('h:mm a')}
            </p>
          )} */}
      </div>
    ) : (
      <>
        {
          <div
            ref={compRef}
            className="services-calendar"
            style={{ background: data.event.color + '99' }}
          >
            {' '}
            <strong> {data.title} </strong>{' '}
            {/* {currentHeight > 50 && currentWidth > 160 && (
              <p className="time-interval">
                {moment(data.event.start).format('h:mm a')} -{' '}
                {moment(data.event.end).format('h:mm a')}
              </p>
            )} */}
            {/* {data?.event?.serviceProvider && (
        <ServiceProviders serviceProviders={[data?.event?.serviceProvider]} />
      )} */}
          </div>
        }
      </>
    );
  };
  return (
    <>
      {!isRoomBooking && customEvents.length > 0 && (
        <EyeIcon
          className="eye-icon-preview cursor-pointer"
          onClick={handleShow}
          title="View Booked Slots"
        />
      )}

      <Modal
        className="petsked-modal-dialog booked-slot-dialog"
        show={showCalendar}
        onHide={handleClose}
        centered
      >
        <Modal.Header className="mb-3" closeButton>
          <Heading size="lg" title={`Booked Slots`} />
        </Modal.Header>
        <Modal.Body>
          <div className="calendar-container">
            {isLoading ? (
              <div className="loading-spinner">Loading calendar...</div>
            ) : (
              <BigCalendarComponent
                localizer={mLocalizer}
                date={selectedDate}
                events={customEvents}
                step={30}
                selectable={true}
                dayLayoutAlgorithm={'no-overlap'}
                views={['agenda']}
                defaultView={'agenda'}
                view={'agenda'}
                components={{
                  event: CustomEvent,
                  week: {
                    event: CustomEvent,
                    header: (settings: any) => settings.localizer.format(settings.date, 'ddd DD'),
                  },
                  month: {
                    header: (settings: any) => settings.localizer.format(settings.date, 'ddd'),
                  },
                  day: {
                    header: (settings: any) =>
                      settings.localizer.format(settings.date, 'MMM DD, dddd'),
                  },
                  toolbar: null,
                }}
                toolbar={false}
                popup={true}
                className={`petsked-calendar agenda-view`}
                longPressThreshold={10}
              />
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
